/**
 * License Ownership Changes — reviews FCC entity-name / FRN changes detected on
 * existing licenses during imports (possible ownership transfers or renames).
 *
 * Backed by GET /v1/licenses/change-log (admin-only), PATCH
 * /v1/licenses/change-log/{id}/reviewed and POST /v1/licenses/change-log/bulk-review.
 * Linked from the Dashboard widget.
 *
 * The queue runs to thousands of rows at 50-state import scale, so it is built around
 * narrowing and then acting in bulk: the shared `FilterBar`, row selection via
 * `useRowSelection`, and a `BulkActionBar` that can escalate from "the rows I ticked"
 * to "everything matching these filters". The escalation is the one action that can
 * touch rows the user never scrolled to, so it alone asks for confirmation, naming
 * both the count and the criteria in words.
 */
import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../services/auth'
import { getChangeLog, markChangeLogReviewed, bulkReviewChangeLog } from '../services/licenses'
import SendMessageModal from '../components/SendMessageModal'
import type {
  FCCLicenseChangeLogEntry,
  ChangeLogFilters,
  ChangeLogSortKey,
} from '../types'
import { Pagination } from '../components/Pagination'
import FilterBar from '../components/FilterBar'
import PageHeading from '../components/PageHeading'
import { OWNERSHIP_HELP } from '../content/pageHelp'
import Modal from '../components/Modal'
import { SortableHeader } from '../components/SortableHeader'
import { useTableSort } from '../hooks/useTableSort'
import { RowCheckbox, SelectAllCheckbox, BulkActionBar } from '../components/BulkSelect'
import { useRowSelection } from '../hooks/useRowSelection'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import {
  isCode, isPageNumber, isStateCode, listStateKey, oneOf, textUpTo, usePageSize, useSessionState,
} from '../hooks/useSessionState'
import { useLicenseFacets, facetFilterOptions } from '../utils/facetOptions'
import {
  REVIEW_STATE_OPTIONS,
  CHANGE_TYPE_OPTIONS,
  DETECTED_WINDOW_OPTIONS,
  windowDays,
} from '../utils/filterOptions'
import { formatLocalDateTime } from '../utils/formatDateTime'


const CHANGE_LABEL: Record<FCCLicenseChangeLogEntry['change_type'], string> = {
  entity_name: 'Name change',
  frn: 'FRN change',
  both: 'Name + FRN',
}

const CHANGE_BADGE: Record<FCCLicenseChangeLogEntry['change_type'], string> = {
  entity_name: 'bg-amber-100 text-amber-800',
  frn: 'bg-orange-100 text-orange-800',
  both: 'bg-red-100 text-red-800',
}

/** old → new value pair, with dashes for missing sides. */
function FromTo({ from, to }: { from: string | null; to: string | null }) {
  if (from == null && to == null) return <span className="text-gray-400">—</span>
  return (
    <span className="whitespace-normal break-words">
      <span className="text-gray-500 line-through">{from || '—'}</span>
      <span className="mx-1.5 text-gray-400">→</span>
      <span className="font-medium text-gray-900">{to || '—'}</span>
    </span>
  )
}

// Filters, page and sort are remembered for the tab (useSessionState), so opening a
// license and coming Back finds the queue as it was left.
const KEY = listStateKey('ownershipChanges')
const SORT_KEYS: readonly ChangeLogSortKey[] = ['detected_at', 'call_sign', 'reviewed']

export default function OwnershipChangesPage() {
  const { user } = useAuth()
  const facets = useLicenseFacets()

  const [items, setItems] = useState<FCCLicenseChangeLogEntry[]>([])
  const [total, setTotal] = useState(0)
  const [maxId, setMaxId] = useState<number | null>(null)
  const [clientLinked, setClientLinked] = useState(0)
  const [page, setPage] = useSessionState(KEY('page'), 1, isPageNumber)
  const [pageSize, setPageSize] = usePageSize('ownershipChanges', 25)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  // An ownership change is only ever a license here — the change log carries no
  // client or lead — so the message targets the USI and the sender types the
  // address. Do Not Contact still resolves: it matches on call sign, licensee
  // name and USI, which is exactly what this row has.
  const [messageUsi, setMessageUsi] = useState<number | null>(null)
  const [markingId, setMarkingId] = useState<number | null>(null)
  const [confirming, setConfirming] = useState<null | { reviewed: boolean }>(null)

  // Filters
  const [search, setSearch] = useSessionState(KEY('search'), '', textUpTo(200))
  const debouncedSearch = useDebouncedValue(search)
  const [reviewState, setReviewState] = useSessionState(
    KEY('reviewState'), 'unreviewed', oneOf(['unreviewed', 'reviewed', 'all']),
  )
  const [changeType, setChangeType] = useSessionState(KEY('changeType'), '', isCode)
  const [state, setState] = useSessionState(KEY('state'), '', isStateCode)
  const [detectedWithin, setDetectedWithin] = useSessionState(KEY('detectedWithin'), '', isCode)

  const { sortKey, sortOrder, handleSort } = useTableSort<ChangeLogSortKey>(
    'detected_at', 'desc',
    {
      descFirst: ['detected_at'], onSortChange: () => setPage(1),
      persist: { key: KEY('sort'), keys: SORT_KEYS },
    },
  )

  /** Any filter change re-reads page 1 — page 3 of the old list is not page 3 of the new one. */
  function applyFilter<T>(set: (v: T) => void) {
    return (value: T) => {
      set(value)
      setPage(1)
    }
  }

  /**
   * The filter set, in exactly the shape the API takes — so the same object drives
   * the list request and a filter-mode bulk request. Two shapes here would be two
   * chances to disagree about what the screen is showing.
   */
  const filters = useMemo<ChangeLogFilters>(
    () => ({
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(reviewState === 'all' ? {} : { reviewed: reviewState === 'reviewed' }),
      ...(changeType ? { change_type: changeType as ChangeLogFilters['change_type'] } : {}),
      ...(state ? { state } : {}),
      ...(windowDays(detectedWithin) ? { detected_within_days: windowDays(detectedWithin) } : {}),
    }),
    [debouncedSearch, reviewState, changeType, state, detectedWithin],
  )

  const selection = useRowSelection<number>(
    items.map((i) => i.id),
    JSON.stringify([filters, page, sortKey, sortOrder]),
  )

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await getChangeLog({
        ...filters,
        sort: sortKey,
        order: sortOrder,
        page,
        page_size: pageSize,
      })
      setItems(res.items)
      setTotal(res.total)
      setMaxId(res.max_id)
      setClientLinked(res.client_linked)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load changes')
    } finally {
      setLoading(false)
    }
  }, [filters, page, pageSize, sortKey, sortOrder])

  useEffect(() => {
    load()
  }, [load])

  if (!user?.is_superuser) return <Navigate to="/" replace />

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  async function handleMarkReviewed(id: number, reviewed: boolean) {
    setMarkingId(id)
    try {
      await markChangeLogReviewed(id, reviewed)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update entry')
    } finally {
      setMarkingId(null)
    }
  }

  async function runBulk(reviewed: boolean) {
    setBusy(true)
    setError('')
    try {
      await bulkReviewChangeLog(
        selection.allMatching
          ? { mode: 'filter', reviewed, filters, max_id: maxId }
          : { mode: 'ids', reviewed, ids: [...selection.selected] },
      )
      selection.clear()
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update changes')
    } finally {
      setBusy(false)
      setConfirming(null)
    }
  }

  /**
   * Selected rows act immediately — the user can see every row they picked, and the
   * action is reversible. "All matching" confirms, because it reaches rows that are
   * not on screen.
   */
  function requestBulk(reviewed: boolean) {
    if (selection.allMatching) setConfirming({ reviewed })
    else runBulk(reviewed)
  }

  /** The active filters as a sentence, for the confirm dialog. */
  function describeFilters(): string {
    const parts: string[] = []
    if (reviewState !== 'all') parts.push(reviewState)
    if (changeType) parts.push(CHANGE_TYPE_OPTIONS.find((o) => o.value === changeType)!.label.toLowerCase())
    else parts.push('ownership changes')
    if (state) parts.push(`in ${state}`)
    if (debouncedSearch) parts.push(`matching “${debouncedSearch}”`)
    const window = DETECTED_WINDOW_OPTIONS.find((o) => o.value === detectedWithin)
    if (detectedWithin && window) parts.push(`detected in the ${window.label.toLowerCase()}`)
    return parts.join(' ')
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="mb-4">
        <Link to="/" className="tap text-sm text-primary-600 hover:underline">
          ← Back to dashboard
        </Link>
      </div>

      <PageHeading
        title="License Ownership Changes" help={OWNERSHIP_HELP} className="mb-4"
        subtitle={<>
          Entity name or FRN changed on an existing license during an FCC import. Review each to
          check for an ownership transfer or company rename. {total.toLocaleString()} shown.
        </>}
      />

      <FilterBar
        searchValue={search}
        onSearchChange={applyFilter(setSearch)}
        searchPlaceholder="Search call sign, name or FRN…"
        searchLabel="Search ownership changes"
        selects={[
          {
            id: 'change-review-state',
            label: 'Filter by review state',
            value: reviewState,
            onChange: applyFilter(setReviewState),
            // The queue is the unreviewed list; that is what "no filter" means here.
            neutralValue: 'unreviewed',
            options: REVIEW_STATE_OPTIONS,
          },
          {
            id: 'change-type',
            label: 'Filter by what changed',
            value: changeType,
            onChange: applyFilter(setChangeType),
            options: CHANGE_TYPE_OPTIONS,
          },
          {
            id: 'change-state',
            label: 'Filter by licensee state',
            value: state,
            onChange: applyFilter(setState),
            options: facetFilterOptions('All states', facets?.states, state),
          },
          {
            id: 'change-detected',
            label: 'Filter by when the change was detected',
            value: detectedWithin,
            onChange: applyFilter(setDetectedWithin),
            options: DETECTED_WINDOW_OPTIONS,
          },
        ]}
      />

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      <BulkActionBar
        count={selection.count}
        total={total}
        pageSize={pageSize}
        allMatching={selection.allMatching}
        onSelectAllMatching={selection.selectAllMatching}
        onClear={selection.clear}
      >
        <button
          type="button"
          onClick={() => requestBulk(true)}
          disabled={busy}
          className="btn-sm btn-primary disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Mark reviewed'}
        </button>
        <button
          type="button"
          onClick={() => requestBulk(false)}
          disabled={busy}
          className="btn-sm btn-secondary disabled:opacity-50"
        >
          Un-review
        </button>
      </BulkActionBar>

      <div className="card overflow-x-auto">
        {loading ? (
          <p className="text-gray-500 text-sm p-4">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-gray-500 text-sm p-4">
            {reviewState === 'unreviewed' && !search && !changeType && !state && !detectedWithin
              ? 'No unreviewed changes. 🎉'
              : 'No changes match these filters.'}
          </p>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                <th className="px-3 py-2 w-px">
                  <SelectAllCheckbox state={selection.pageState} onToggle={selection.togglePage} />
                </th>
                <SortableHeader
                  sortKey="detected_at"
                  currentSortKey={sortKey}
                  currentSortOrder={sortOrder}
                  onSort={handleSort}
                  className="px-3 py-2"
                >
                  Detected
                </SortableHeader>
                <SortableHeader
                  sortKey="call_sign"
                  currentSortKey={sortKey}
                  currentSortOrder={sortOrder}
                  onSort={handleSort}
                  className="px-3 py-2"
                >
                  Call Sign
                </SortableHeader>
                <th className="px-3 py-2 font-medium">Change</th>
                <th className="px-3 py-2 font-medium">Entity name</th>
                <th className="px-3 py-2 font-medium">FRN</th>
                <SortableHeader
                  sortKey="reviewed"
                  currentSortKey={sortKey}
                  currentSortOrder={sortOrder}
                  onSort={handleSort}
                  className="px-3 py-2 text-right"
                >
                  Reviewed
                </SortableHeader>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((row) => (
                <tr key={row.id} className={row.reviewed ? 'opacity-60' : ''}>
                  <td className="px-3 py-2 w-px">
                    <RowCheckbox
                      checked={selection.isSelected(row.id)}
                      onChange={() => selection.toggle(row.id)}
                      label={row.call_sign || `change ${row.id}`}
                    />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                    {formatLocalDateTime(row.detected_at)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap font-mono">
                    {row.call_sign ? (
                      <Link
                        to={`/licenses/${row.call_sign}`}
                        className="tap text-primary-600 hover:underline"
                      >
                        {row.call_sign}
                      </Link>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-semibold ${CHANGE_BADGE[row.change_type]}`}
                    >
                      {CHANGE_LABEL[row.change_type]}
                    </span>
                  </td>
                  <td className="px-3 py-2 max-w-xs">
                    {row.change_type === 'frn' ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      <FromTo from={row.old_entity_name} to={row.new_entity_name} />
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono whitespace-nowrap">
                    {row.change_type === 'entity_name' ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      <FromTo from={row.old_frn} to={row.new_frn} />
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    <button
                      onClick={() => setMessageUsi(row.unique_system_identifier)}
                      className="tap text-xs text-primary-600 underline mr-3"
                    >
                      Send message
                    </button>
                    {row.reviewed ? (
                      <div className="flex flex-col items-end gap-0.5">
                        <span className="text-xs text-green-700 font-medium">
                          {/* Entries reviewed before attribution existed have no name
                              or date. Saying so beats inventing one. */}
                          {row.reviewed_by_name
                            ? `Reviewed by ${row.reviewed_by_name}`
                            : 'Reviewed'}
                        </span>
                        {row.reviewed_at && (
                          <span className="text-xs text-gray-400">
                            {formatLocalDateTime(row.reviewed_at)}
                          </span>
                        )}
                        <button
                          onClick={() => handleMarkReviewed(row.id, false)}
                          disabled={markingId === row.id}
                          className="tap text-xs text-gray-500 underline disabled:opacity-50"
                        >
                          {markingId === row.id ? 'Saving…' : 'Un-review'}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleMarkReviewed(row.id, true)}
                        disabled={markingId === row.id}
                        className="btn-sm btn-secondary disabled:opacity-50"
                      >
                        {markingId === row.id ? 'Saving…' : 'Mark reviewed'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Pagination
        page={page} totalPages={totalPages} onPageChange={setPage}
        total={total} pageSize={pageSize}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1) }}
      />

      {confirming && (
        <Modal
          titleId="bulk-review-confirm"
          title={
            confirming.reviewed
              ? `Mark ${total.toLocaleString()} changes reviewed?`
              : `Un-review ${total.toLocaleString()} changes?`
          }
          onClose={() => setConfirming(null)}
          maxWidth="max-w-md"
        >
          <p className="text-sm text-gray-600">
            {/* Naming the criteria, not just the count: the whole risk of this action
                is acting on a set the user has only partly seen. */}
            This applies to all {total.toLocaleString()} {describeFilters()} — including rows on
            pages you have not opened.
          </p>
          {clientLinked > 0 && (
            <p className="mt-2 text-sm text-amber-800">
              {/* Clearing these also clears the "Ownership changed" badges on the
                  Clients list and client detail pages. Better said here than
                  discovered afterwards. */}
              <strong>{clientLinked.toLocaleString()}</strong> of them{' '}
              {clientLinked === 1 ? 'is' : 'are'} on a license a client holds
              {confirming.reviewed
                ? ', so their “Ownership changed” badges will clear too.'
                : ', so their “Ownership changed” badges will come back.'}
            </p>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirming(null)}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => runBulk(confirming.reviewed)}
              disabled={busy}
              className="btn btn-primary disabled:opacity-50"
            >
              {busy
                ? 'Saving…'
                : confirming.reviewed
                  ? `Mark ${total.toLocaleString()} reviewed`
                  : `Un-review ${total.toLocaleString()}`}
            </button>
          </div>
        </Modal>
      )}

      {messageUsi !== null && (
        <SendMessageModal
          onClose={() => setMessageUsi(null)}
          fccUsi={messageUsi}
          onSent={() => setMessageUsi(null)}
        />
      )}
    </div>
  )
}
